import { useState } from 'react';
import { 
  Share2, Copy, Check, MessageCircle, Twitter, Facebook, 
  Link2, Mail, Send
} from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { useTheme } from '../../context/ThemeContext';
import { toast } from 'sonner';

// WhatsApp Icon Component
const WhatsAppIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

// Telegram Icon Component
const TelegramIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
  </svg>
);

// Generate share URL for different platforms
const generateShareUrl = (platform, { title, description, url, price }) => {
  const text = `${title}\n${description}\n💰 ${price}\n\n`;
  const encodedText = encodeURIComponent(text);
  const encodedUrl = encodeURIComponent(url);
  
  switch (platform) {
    case 'whatsapp':
      return `https://wa.me/?text=${encodedText}${encodedUrl}`;
    case 'twitter':
      return `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`;
    case 'facebook':
      return `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedText}`;
    case 'telegram':
      return `https://t.me/share/url?url=${encodedUrl}&text=${encodedText}`;
    case 'email':
      return `mailto:?subject=${encodeURIComponent(title)}&body=${encodedText}${encodedUrl}`;
    default:
      return url;
  }
};

// Share Button Component
export function ShareButton({ platform, shareData, className = '' }) {
  const { language } = useLanguage();
  const { isDarkMode } = useTheme();
  
  const platformConfig = {
    whatsapp: {
      icon: WhatsAppIcon,
      label: { ar: 'واتساب', en: 'WhatsApp' },
      color: 'bg-[#25D366] hover:bg-[#128C7E]',
      textColor: 'text-white'
    },
    twitter: {
      icon: Twitter,
      label: { ar: 'تويتر', en: 'Twitter' },
      color: 'bg-[#1DA1F2] hover:bg-[#0c85d0]',
      textColor: 'text-white'
    },
    facebook: {
      icon: Facebook,
      label: { ar: 'فيسبوك', en: 'Facebook' },
      color: 'bg-[#1877F2] hover:bg-[#0d65d9]',
      textColor: 'text-white'
    },
    telegram: {
      icon: TelegramIcon,
      label: { ar: 'تيليجرام', en: 'Telegram' },
      color: 'bg-[#0088cc] hover:bg-[#006699]',
      textColor: 'text-white'
    },
    email: {
      icon: Mail,
      label: { ar: 'بريد إلكتروني', en: 'Email' },
      color: isDarkMode ? 'bg-gray-600 hover:bg-gray-500' : 'bg-gray-500 hover:bg-gray-600',
      textColor: 'text-white'
    }
  };
  
  const config = platformConfig[platform];
  if (!config) return null;
  
  const Icon = config.icon;
  const shareUrl = generateShareUrl(platform, shareData);
  
  const handleShare = () => {
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };
  
  return (
    <button
      onClick={handleShare}
      className={`flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl font-medium transition-all transform hover:scale-105 ${config.color} ${config.textColor} ${className}`}
      data-testid={`share-${platform}-btn`}
    >
      <Icon className="h-5 w-5" />
      <span className="text-sm">{language === 'ar' ? config.label.ar : config.label.en}</span>
    </button>
  );
}

// Copy Link Button
export function CopyLinkButton({ url, className = '' }) {
  const { language } = useLanguage();
  const { isDarkMode } = useTheme();
  const [copied, setCopied] = useState(false);
  
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success(language === 'ar' ? 'تم نسخ الرابط!' : 'Link copied!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error(language === 'ar' ? 'فشل نسخ الرابط' : 'Failed to copy link');
    }
  };
  
  return (
    <button
      onClick={handleCopy}
      className={`flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl font-medium transition-all border-2 ${
        copied 
          ? 'bg-green-100 border-green-500 text-green-700'
          : isDarkMode 
            ? 'border-[#475569] text-gray-300 hover:border-[#1E9BD9] hover:text-[#1E9BD9]'
            : 'border-gray-300 text-gray-700 hover:border-[#1E9BD9] hover:text-[#1E9BD9]'
      } ${className}`}
      data-testid="copy-link-btn"
    >
      {copied ? (
        <>
          <Check className="h-5 w-5" />
          <span className="text-sm">{language === 'ar' ? 'تم النسخ!' : 'Copied!'}</span>
        </>
      ) : (
        <>
          <Copy className="h-5 w-5" />
          <span className="text-sm">{language === 'ar' ? 'نسخ الرابط' : 'Copy Link'}</span>
        </>
      )}
    </button>
  );
}

// Share Dropdown/Modal Component
export default function ShareButtons({ 
  title, 
  description, 
  price, 
  url,
  compact = false,
  showLabel = true 
}) {
  const { language } = useLanguage();
  const { isDarkMode } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  
  // Generate a preview URL (in production this would be the actual ad URL)
  const shareUrl = url || window.location.href;
  
  const shareData = {
    title,
    description: description || '',
    price: price ? `${Number(price).toLocaleString()} ${language === 'ar' ? 'درهم' : 'AED'}` : '',
    url: shareUrl
  };
  
  // Native share if available
  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: title,
          text: `${title}\n${description}\n💰 ${shareData.price}`,
          url: shareUrl
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          setIsOpen(true);
        }
      }
    } else {
      setIsOpen(true);
    }
  };
  
  if (compact) {
    return (
      <div className="relative">
        <button
          onClick={handleNativeShare}
          className={`flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl font-medium transition-all ${
            isDarkMode 
              ? 'bg-[#334155] hover:bg-[#475569] text-white'
              : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
          }`}
          data-testid="share-btn"
        >
          <Share2 className="h-5 w-5" />
          {showLabel && (
            <span className="text-sm">{language === 'ar' ? 'مشاركة' : 'Share'}</span>
          )}
        </button>
        
        {/* Dropdown */}
        {isOpen && (
          <>
            <div 
              className="fixed inset-0 z-40"
              onClick={() => setIsOpen(false)}
            />
            <div className={`absolute top-full mt-2 right-0 z-50 w-56 rounded-xl shadow-xl border p-3 ${
              isDarkMode ? 'bg-[#1E293B] border-[#334155]' : 'bg-white border-gray-200'
            }`}>
              <div className="space-y-2">
                <ShareButton platform="whatsapp" shareData={shareData} className="w-full" />
                <ShareButton platform="twitter" shareData={shareData} className="w-full" />
                <ShareButton platform="telegram" shareData={shareData} className="w-full" />
                <ShareButton platform="facebook" shareData={shareData} className="w-full" />
                <div className="border-t pt-2 mt-2">
                  <CopyLinkButton url={shareUrl} className="w-full" />
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    );
  }
  
  // Full share panel
  return (
    <div className={`rounded-xl p-4 border ${isDarkMode ? 'bg-[#1E293B] border-[#334155]' : 'bg-gray-50 border-gray-200'}`}>
      <div className="flex items-center gap-2 mb-4">
        <Share2 className={`h-5 w-5 ${isDarkMode ? 'text-[#1E9BD9]' : 'text-[#1E9BD9]'}`} />
        <h3 className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
          {language === 'ar' ? 'شارك الإعلان' : 'Share Ad'}
        </h3>
      </div>
      
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
        <ShareButton platform="whatsapp" shareData={shareData} />
        <ShareButton platform="twitter" shareData={shareData} />
        <ShareButton platform="telegram" shareData={shareData} />
        <ShareButton platform="facebook" shareData={shareData} />
      </div>
      
      <CopyLinkButton url={shareUrl} className="w-full" />
    </div>
  );
}
